import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { MenuAction } from 'models/types';
import CssBaseline from '@mui/material/CssBaseline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import UserNavbarHeader from 'components/UserNavbarHeader';
import FooterLayout from 'layouts/FooterLayout';
import SmeAppBar from 'containers/SmeAppBar';
import Logo from 'logos/Logo';
import CurrentUser from 'auth/currentUser';
import { drawerWidth } from 'atlasConfig';

const PREFIX = 'AdminApp';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  appBar: `${PREFIX}-appBar`,
  logo: `${PREFIX}-logo`,
  collapse: `${PREFIX}-collapse`,
  appBarShift: `${PREFIX}-appBarShift`,
  menuButton: `${PREFIX}-menuButton`,
  menuButtonHidden: `${PREFIX}-menuButtonHidden`,
  title: `${PREFIX}-title`,
  appBarSpacer: `${PREFIX}-appBarSpacer`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  container: `${PREFIX}-container`,
  paper: `${PREFIX}-paper`,
  fixedHeight: `${PREFIX}-fixedHeight`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  drawerPaperClose: `${PREFIX}-drawerPaperClose`,
  toolbarIcon: `${PREFIX}-toolbarIcon`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
  },

  [`& .${classes.toolbar}`]: {
    background: 'white',
    color: '#121212',
    paddingRight: 24, // keep right padding when drawer closed
  },

  [`& .${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  [`& .${classes.appBarShift}`]: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  [`& .${classes.logo}`]: {
    margin: '0 auto',
  },

  [`& .${classes.collapse}`]: {
    position: 'absolute',
  },

  [`& .${classes.menuButton}`]: {
    marginRight: 36,
  },

  [`& .${classes.menuButtonHidden}`]: {
    display: 'none',
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },

  [`& .${classes.appBarSpacer}`]: theme.mixins.toolbar,

  [`& .${classes.contentContainer}`]: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    flex: 1,
    display: 'flex',
  },

  [`& .${classes.container}`]: {
    position: 'relative',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },

  [`& .${classes.fixedHeight}`]: {
    height: 240,
  },

  [`& .${classes.drawerPaper}`]: {
    backgroundColor: '#121212',
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  [`& .${classes.drawerPaperClose}`]: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  },

  [`& .${classes.toolbarIcon}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    color: 'white',
    backgroundColor: '#4050b5',
    ...theme.mixins.toolbar
  }
}));

interface OnRender {
  (actions?: MenuAction[]): JSX.Element;
}

interface Props {
  onRenderTfp: OnRender;
  onRenderSme: OnRender;
  children: React.ReactNode;
}

export default function AdminApp(props: Props): JSX.Element {
  const currentUser = CurrentUser.getInstance();
  const [role, setRole] = useState(currentUser.getRole() || 'admin');
  const [open, setOpen] = useState(true);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleRoleChange = (role: string) => {
    currentUser.setRole(role);
    setRole(role);
  };

  const actions: MenuAction[] = [
    { label: 'Admin View', onClick: () => handleRoleChange('admin') },
    { label: 'Preview SME view', onClick: () => handleRoleChange('sme') }
  ];

  const renderAdminView = () => (
    <Root className={classes.root}>
      <CssBaseline />
      <SmeAppBar
        open={open}
        title="Admin Panel"
        onDrawerOpen={handleDrawerOpen}
        actions={actions}
      />
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
        }}
        open={open}
      >
        <div className={classes.toolbarIcon}>
          <Logo className={classes.logo} color="white" height={75}/>
          <IconButton className={classes.collapse} onClick={handleDrawerClose} color="inherit">
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <UserNavbarHeader
          open={open}
        />
      </Drawer>

      <main className={classes.contentContainer}>
        <div className={classes.appBarSpacer} />
        <div className={classes.content}>
          {props.children}
        </div>
        <FooterLayout />
      </main>
    </Root>
  );

  if (role === 'tfp') {
    return props.onRenderTfp(actions);
  } else if (role === 'sme') {
    return props.onRenderSme(actions);
  } else {
    return renderAdminView();
  }
}
